import { isMirrored } from './model'
import type { AxisFreedom, DofKey, HumanoidModel, MassItem, PointKey, SegmentKey } from './model'
import type { AxisKey } from './model'

/**
 * Änderungen am Modell — als Datensatz, nicht als zweites Modell.
 *
 * DIE ENTSCHEIDENDE BAUFORM: eine Änderung ist ein EINTRAG, kein neues Modell.
 * `{ 'point:knee:z': -859 }` sagt „die Knieachse liegt bei −859 mm"; alles
 * andere kommt weiterhin aus dem Grundmodell.
 *
 * WARUM NICHT EINFACH DAS MODELL KOPIEREN UND ÄNDERN:
 *
 *  · Man sieht sonst nicht mehr, WAS geändert wurde. Ein kopiertes Modell mit
 *    einem anderen Wert ist von einem anderen Modell nicht zu unterscheiden.
 *    Hier steht die Abweichung als Liste da und lässt sich einzeln zurücknehmen.
 *  · Der Grundstand bleibt erhalten. „Zurücksetzen" ist das Leeren einer Liste
 *    und nicht das Wiederherstellen aus einer Sicherung.
 *  · Ein Wechsel des Roboters trägt die Änderungen mit, soweit die Schlüssel
 *    passen — und lässt sie fallen, wo sie es nicht tun.
 *
 * DER RECHENKERN SIEHT DAVON NICHTS. `applyEdits` liefert ein gewöhnliches
 * `HumanoidModel`; jede Rechnung bleibt eine reine Funktion über einem Modell.
 */

/**
 * Der Schlüssel einer Änderung.
 *
 * Als Zeichenkette und nicht als verschachtelte Struktur, damit die Liste flach
 * bleibt: `Object.keys(edits).length` ist die Zahl der Abweichungen, und das
 * Zurücknehmen einer einzelnen ist ein `delete`.
 *
 *   `point:<punkt>:<x|y|z>`      Gelenkpunkt in Millimetern, beide Seiten
 *   `point:<punkt>:L:<x|y|z>`    dasselbe Mass, aber nur links — eine GEWOLLTE
 *                                Unsymmetrie. Sie entsteht nie nebenbei: die
 *                                Oberfläche fragt nach, bevor sie einen solchen
 *                                Eintrag anlegt.
 *   `mass:<segment>:kg`          Segmentmasse in Kilogramm
 *   `mass:<segment>:com`         Schwerpunktlage, Anteil der Segmentlänge
 *   `mass:<segment>:gyration`    Trägheitsradius, Anteil der Segmentlänge
 *   `dof:<gelenk>:<x|y|z>:lo`    untere Winkelgrenze in Grad
 *   `dof:<gelenk>:<x|y|z>:hi`    obere Winkelgrenze in Grad
 *   `dof:<gelenk>:<x|y|z>:moment` zulässiges Moment in Nm
 */
export type EditKey = string

export type Edits = Record<EditKey, number>

export const pointEdit = (point: PointKey, axis: 'x' | 'y' | 'z'): EditKey =>
  `point:${point}:${axis}`

/**
 * Derselbe Punkt, aber nur auf der linken Seite.
 *
 * Es gibt bewusst KEINEN Schlüssel für „nur rechts": der Punktsatz IST die
 * rechte Seite und zugleich die gemeinsame Vorgabe. Wer rechts ändert, ändert
 * beide, solange links nichts Eigenes trägt — und genau das ist die Frage, die
 * die Oberfläche stellt.
 */
export const sidePointEdit = (point: PointKey, axis: 'x' | 'y' | 'z'): EditKey =>
  `point:${point}:L:${axis}`

/** Gehört dieser Schlüssel zur linken Seite allein? */
export const isSideEdit = (key: EditKey): boolean => key.startsWith('point:') && key.split(':')[2] === 'L'

export const massEdit = (segment: SegmentKey, field: 'kg' | 'com' | 'gyration'): EditKey =>
  `mass:${segment}:${field}`

export const dofEdit = (joint: DofKey, axis: AxisKey, field: 'lo' | 'hi' | 'moment'): EditKey =>
  `dof:${joint}:${axis}:${field}`

/* -------------------------------------------------------------------------
   Grenzen
   ------------------------------------------------------------------------- */

/**
 * Was ein Feld annehmen darf.
 *
 * Nicht um zu bevormunden, sondern um ENTARTETE MODELLE ZU VERHINDERN. Ein
 * Oberschenkel der Länge null bringt die Kinematik zum Stillstand, eine Masse
 * von null teilt in der Dynamik durch sich selbst. Die Grenzen sind weit — sie
 * fangen den Zahlendreher ab, nicht die ungewöhnliche Konstruktion.
 */
export interface EditRange {
  min: number
  max: number
  step: number
  unit: string
  digits: number
}

export const POINT_RANGE: EditRange = { min: -1600, max: 900, step: 1, unit: 'mm', digits: 0 }

export const MASS_RANGES: Record<'kg' | 'com' | 'gyration', EditRange> = {
  kg: { min: 0.05, max: 60, step: 0.1, unit: 'kg', digits: 2 },
  com: { min: 0.02, max: 0.98, step: 0.001, unit: '', digits: 3 },
  gyration: { min: 0.02, max: 0.9, step: 0.001, unit: '', digits: 3 },
}

export const DOF_RANGES: Record<'lo' | 'hi' | 'moment', EditRange> = {
  lo: { min: -180, max: 180, step: 1, unit: '°', digits: 0 },
  hi: { min: -180, max: 180, step: 1, unit: '°', digits: 0 },
  moment: { min: 0, max: 1200, step: 1, unit: 'Nm', digits: 0 },
}

const clamp = (value: number, range: EditRange): number =>
  Math.min(range.max, Math.max(range.min, value))

/** Zu welchem Bereich ein Schlüssel gehört. `null` bei unbekanntem Schlüssel. */
export function rangeFor(key: EditKey): EditRange | null {
  const parts = key.split(':')
  if (parts[0] === 'point') return POINT_RANGE
  if (parts[0] === 'mass') return MASS_RANGES[parts[2] as 'kg' | 'com' | 'gyration'] ?? null
  if (parts[0] === 'dof') return DOF_RANGES[parts[3] as 'lo' | 'hi' | 'moment'] ?? null
  return null
}

/* -------------------------------------------------------------------------
   Anwenden
   ------------------------------------------------------------------------- */

/**
 * Legt die Änderungen über ein Modell.
 *
 * Kopiert NUR, was berührt wird. Ein Modell hat sechzehn Punkte, elf Gelenke
 * und acht Massen; sie alle zu kopieren, weil eine Knieachse verschoben wurde,
 * würde bei jedem Tastendruck den gesamten Baum neu bewerten — der
 * Simulationskontext vergleicht Verweise.
 *
 * Unbekannte Schlüssel werden STILL ÜBERGANGEN. Sie entstehen beim Wechsel
 * zwischen Modellen mit verschiedenen Punktesätzen, und ein Fehler wäre dort
 * die falsche Antwort: der Eintrag gehört zu einem anderen Roboter, das ist
 * kein Defekt.
 */
export function applyEdits(model: HumanoidModel, edits: Edits): HumanoidModel {
  const keys = Object.keys(edits)
  if (keys.length === 0) return model

  let points: HumanoidModel['points'] | null = null
  let asym: HumanoidModel['asym'] | null = null
  let masses: HumanoidModel['masses'] | null = null
  let dofs: HumanoidModel['dofs'] | null = null

  for (const key of keys) {
    const parts = key.split(':')
    const range = rangeFor(key)
    if (!range) continue
    const value = clamp(edits[key], range)

    if (parts[0] === 'point' && parts.length === 4 && parts[2] === 'L') {
      /* Nur links. Der Eintrag liegt NEBEN dem Punkt und ersetzt ihn nicht:
         so bleibt die gemeinsame Zahl ablesbar, und wer die Unsymmetrie
         zurücknimmt, bekommt wieder beide Seiten gleich. */
      const point = parts[1] as PointKey
      const axis = parts[3] as 'x' | 'y' | 'z'
      const source = model.points[point]
      if (!source || source.fixed || !isMirrored(point)) continue
      asym ??= { ...(model.asym ?? {}) }
      asym[point] = { ...asym[point], [axis]: value }
      continue
    }

    if (parts[0] === 'point') {
      const point = parts[1] as PointKey
      const axis = parts[2] as 'x' | 'y' | 'z'
      const source = (points ?? model.points)[point]
      /* Der Nullpunkt bleibt der Nullpunkt. Ihn zu verschieben hiesse, alle
         anderen Punkte um denselben Betrag mitzuverschieben — dasselbe Modell,
         nur mit verstellter Anzeige. */
      if (!source || source.fixed) continue
      points ??= { ...model.points }
      points[point] = { ...source, [axis]: value }
      continue
    }

    if (parts[0] === 'mass') {
      const segment = parts[1] as SegmentKey
      const field = parts[2] as keyof MassItem
      const source = (masses ?? model.masses)[segment]
      if (!source) continue
      masses ??= { ...model.masses }
      masses[segment] = { ...source, [field]: value }
      continue
    }

    if (parts[0] === 'dof') {
      const joint = parts[1] as DofKey
      const axis = parts[2] as AxisKey
      const field = parts[3] as keyof AxisFreedom
      const dof = (dofs ?? model.dofs)[joint]
      if (!dof) continue
      const freedom = dof[axis]
      /* Eine Achse, die es nicht gibt, bekommt auch keine Grenze. Sie hier
         anzulegen hiesse, dem Roboter still einen Freiheitsgrad zu schenken. */
      if (!freedom) continue
      dofs ??= { ...model.dofs }
      dofs[joint] = { ...dof, [axis]: { ...freedom, [field]: value } }
    }
  }

  if (!points && !asym && !masses && !dofs) return model

  return {
    ...model,
    id: `${model.id}+`,
    points: points ?? model.points,
    asym: asym ?? model.asym,
    masses: masses ?? model.masses,
    dofs: dofs ?? model.dofs,
  }
}

/**
 * Was ein Schlüssel im GRUNDMODELL bedeutet — der Wert, gegen den verglichen wird.
 *
 * Die Oberfläche zeigt neben jedem geänderten Feld den Ausgangswert. Ohne ihn
 * ist eine Änderung nicht zurückzunehmen, ohne die Datei aufzumachen.
 */
export function baseValue(model: HumanoidModel, key: EditKey): number | null {
  const parts = key.split(':')

  if (parts[0] === 'point') {
    const point = model.points[parts[1] as PointKey]
    if (!point) return null
    /* Der Vergleichswert einer Seitenabweichung ist die GEMEINSAME Zahl: eine
       linke Seite, die genau dort liegt, wo die rechte liegt, ist keine
       Unsymmetrie. */
    return parts.length === 4 ? point[parts[3] as 'x' | 'y' | 'z'] : point[parts[2] as 'x' | 'y' | 'z']
  }
  if (parts[0] === 'mass') {
    const item = model.masses[parts[1] as SegmentKey]
    if (!item) return null
    const value = item[parts[2] as 'kg' | 'com' | 'gyration']
    return typeof value === 'number' ? value : null
  }
  if (parts[0] === 'dof') {
    const dof = model.dofs[parts[1] as DofKey]
    const freedom = dof?.[parts[2] as AxisKey]
    return freedom ? freedom[parts[3] as keyof AxisFreedom] : null
  }
  return null
}

/**
 * Wirft Änderungen weg, die nichts mehr ändern.
 *
 * Wer einen Wert verstellt und wieder auf den Ausgangswert setzt, hat nichts
 * geändert — und soll auch keine Zeile in der Änderungsliste behalten. Ohne das
 * zählt die Oberfläche „drei Änderungen", von denen zwei keine sind.
 */
export function pruneEdits(model: HumanoidModel, edits: Edits): Edits {
  const out: Edits = {}
  for (const key of Object.keys(edits)) {
    const range = rangeFor(key)
    if (!range) continue

    /*
      EINE SEITENABWEICHUNG VERGLEICHT SICH GEGEN DIE GEÄNDERTE GEMEINSAME ZAHL,
      nicht gegen den Grundstand. Wer links auf −880 setzt und danach rechts
      ebenfalls auf −880, hat keine Unsymmetrie mehr — und soll auch keine
      Kennzeichnung behalten. Gegen den Grundstand verglichen bliebe sie stehen.
    */
    const parts = key.split(':')
    const shared =
      parts[0] === 'point' && parts.length === 4
        ? edits[pointEdit(parts[1] as PointKey, parts[3] as 'x' | 'y' | 'z')]
        : undefined
    const base = shared ?? baseValue(model, key)
    if (base === null || base === undefined) continue
    /* Auf die Anzeigegenauigkeit gerundet vergleichen: 8,000000001 kg ist keine
       Änderung gegenüber 8 kg, sondern eine Gleitkommazahl. */
    const factor = Math.pow(10, range.digits)
    if (Math.round(edits[key] * factor) === Math.round(base * factor)) continue
    out[key] = edits[key]
  }
  return out
}
